<template>
  <a-popover
    :visible="visible"
    :get-popup-container="() => ($refs.transmission as HTMLElement)"
    overlay-class-name="transmission-popover"
    trigger="click"
    destroy-tooltip-on-hide
    :overlay-style="{
      '--left': `-${popLeft}px`,
      '--smallScreenLeft': `-${smallScreenLeftPopLeft}px`,
    }"
    @visible-change="visibleChange"
  >
    <template #content>
      <TransmissionTabs :task-id="taskId" @close="notificationBarClicked" />
    </template>
    <div
      ref="transmission"
      :class="['transmission-container-content', isSelected ? 'header-icon-selected' : '']"
      @click.stop="notificationBarClicked"
    >
      <img class="header-icon" :src="iconTransmission" alt="" />
      <sup v-show="isShow" :class="badgeClass">{{ badgeClass === BADGE_CLASS_ABNORMAL ? '!' : countContent }}</sup>
    </div>
  </a-popover>
</template>
<script lang="ts" setup>
  import { onMounted, ref, computed, watch } from 'vue'
  import iconTransmission from '@assets/svgs/preload-icon-transmission.svg'
  import type { TaskCountInfo } from '@/core/transmission/TransmissionNoticeManager'
  import { useOtrack } from '@/core/compositions/Otrack'
  import { TrackEventMap } from '@core/TrackConstants'
  import { INSTANCE as transmissionNoticeManager, NoticeReadState } from '@/core/transmission/TransmissionNoticeManager'
  import { useSharedActiveMainHeaderItem, MainHeaderItems } from '@/core/compositions/ActiveMainHeaderItem'
  import TransmissionTabs from '@components/transmission/TransmissionTabs.vue'
  import { TaskState } from '@/core/transmission/ITask'
  import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock-upgrade'
  import { useSharedScreen } from '@/core/compositions/Screen'
  import { delay } from 'lodash-es'

  const { isStandardScreen, isLargeScreen } = useSharedScreen()
  const { currentSelectedItem, updateCurrentSelectedItem, onCurrentSelectedItemUpdated } =
    useSharedActiveMainHeaderItem()

  const isSelected = computed(() => currentSelectedItem.value === MainHeaderItems.TRANSMISSION_LIST)
  const countContent = ref('')
  const isShow = ref(false)
  const MAX_COUNT_NUMBER = 999
  const taskCountInfo = ref<TaskCountInfo>()
  const visible = ref(false)
  const transmission = ref<HTMLDivElement>()
  const DEFAULT_LEFT = 298
  const DEFAULT_SMALL_SCREEN_LEFT = 352
  const popLeft = ref(DEFAULT_LEFT)
  const smallScreenLeftPopLeft = ref(DEFAULT_SMALL_SCREEN_LEFT)
  const TRANSMISSION_POPOVER_WIDTH = 560
  const POPOVER_PADDING = 16
  const MINIMUM_WIDTH = 1024
  const ARIES_PAD_WIDTH = 1120
  const HEADER_PADDING_GAP = 0
  const taskId = ref<string>()

  // const setActiveTask = (item?: MainHeaderItems, extraQuery = '') => {
  //   if (item !== MainHeaderItems.TRANSMISSION_LIST) return

  //   const params = new URLSearchParams(extraQuery)

  //   taskId.value = params.get('taskId') ?? undefined

  //   delay(() => {
  //     taskId.value = undefined
  //   }, 3000)
  // }

  // onCurrentSelectedItemUpdated.value.add(setActiveTask)

  watch(visible, (newValue) => {
    // 每次弹出传输弹窗进行位移计算
    if (newValue && transmission.value) {
      const isSmallScreen = window.innerWidth < ARIES_PAD_WIDTH

      // 因为大小屏边距不同，同时会存在拖动浏览器边框的情况，所以需要大小屏分开计算
      if (isSmallScreen) {
        smallScreenLeftPopLeft.value =
          TRANSMISSION_POPOVER_WIDTH -
          (window.innerWidth - transmission.value.getBoundingClientRect().left) +
          POPOVER_PADDING
        popLeft.value = smallScreenLeftPopLeft.value - HEADER_PADDING_GAP
      } else {
        popLeft.value =
          TRANSMISSION_POPOVER_WIDTH -
          (window.innerWidth - transmission.value.getBoundingClientRect().left) +
          POPOVER_PADDING
        smallScreenLeftPopLeft.value = popLeft.value + HEADER_PADDING_GAP
      }

      !isStandardScreen.value &&
        disableBodyScroll(transmission.value, {
          allowTouchMove: () => true,
        })
    } else {
      clearAllBodyScrollLocks()
    }
  })

  watch(isSelected, (value) => {
    visible.value = value
  })

  const visibleChange = (newVisible: boolean) => {
    visible.value = newVisible

    if (!newVisible) {
      updateCurrentSelectedItem(undefined)

      if (taskCountInfo.value) {
        taskCountInfo.value.isRead = NoticeReadState.READ
        hasNewErrorTask.value = false
      }
    }
  }

  watch(isLargeScreen, () => {
    isSelected.value && updateCurrentSelectedItem(undefined)
  })

  const { track } = useOtrack()

  const notificationBarClicked = () => {
    updateCurrentSelectedItem(MainHeaderItems.TRANSMISSION_LIST)

    if (isSelected.value) {
      track({
        eventId: TrackEventMap.TRANSMISSION_PANEL_CLICK,
      })
    }
  }

  const hasNewErrorTask = ref(false)

  const calculateUnfinishedTaskNum = (taskCount: TaskCountInfo) => {
    const taskNum =
      taskCount.downloadTotal -
      taskCount.downloadSuccess -
      taskCount.downloadError +
      taskCount.uploadTotal -
      taskCount.uploadSuccess -
      taskCount.uploadError

    if (taskNum > 0 && taskNum <= MAX_COUNT_NUMBER) return taskNum.toString()

    return taskNum > MAX_COUNT_NUMBER ? '•••' : ''
  }

  const BADGE_CLASS_ABNORMAL = 'abnormal'
  const BADGE_CLASS_DOT = 'dot'
  const BADGE_CLASS_COUNT = 'count'

  const badgeClass = computed(() => {
    if (hasNewErrorTask.value && taskCountInfo.value?.isRead !== NoticeReadState.READ) {
      return BADGE_CLASS_ABNORMAL
    } else if (countContent.value.length === 0) {
      return BADGE_CLASS_DOT
    } else {
      return BADGE_CLASS_COUNT
    }
  })

  onMounted(() => {
    transmissionNoticeManager.setNoticeListener({
      onChange: (taskCount: TaskCountInfo, taskState: TaskState) => {
        taskState === TaskState.IDLE &&
          !isSelected.value &&
          updateCurrentSelectedItem(MainHeaderItems.TRANSMISSION_LIST)

        if (taskState === TaskState.FAILED) {
          hasNewErrorTask.value = true
        }

        taskCountInfo.value = taskCount

        if (taskCount.isRead === NoticeReadState.UNREAD) {
          isShow.value = true

          if (isAllFinished(taskCount)) {
            countContent.value = ''
          } else {
            countContent.value = calculateUnfinishedTaskNum(taskCount)
          }
        } else if (taskCount.isRead === NoticeReadState.READ) {
          if (isAllFinished(taskCount)) {
            isShow.value = false
          } else {
            isShow.value = true

            countContent.value = calculateUnfinishedTaskNum(taskCount)
          }
        }
      },
    })
  })

  const isAllFinished = (taskCount: TaskCountInfo) =>
    taskCount.downloadTotal === taskCount.downloadSuccess + taskCount.downloadError &&
    taskCount.uploadTotal === taskCount.uploadSuccess + taskCount.uploadError

  defineExpose({
    notificationBarClicked,
  })
</script>
<style lang="stylus" scoped>

  .transmission-container-content
    width 100%
    height 100%
    flex-all-center()
  .header-icon
    rect(20px)
    cursor pointer
    vertical-align super
  sup
    position absolute
    background-color #e32e27
  .dot
    rect(6px)
    top 5px
    left 24px
    border-radius 3px
  .count
  .abnormal
    top -1px
    left 23px
    height 16px
    line-height 1.4
    border-radius 8px
    font-size 12px
    color #fff
    min-width 16px
    padding 0 4px
    text-align center
    white-space nowrap
  .count
    background-color rgba(235, 59, 47, 1)
  .header-icon-selected
    background-color rgba(84, 86, 104, 0.06)
    border-radius 6px
  :deep(.ant-popover)
    z-index $zIndexModal
  :deep(.transmission-popover)
    left var(--left) !important
    width transmission_popover_width
    height 332px
    border-radius 12px
    top 29px !important
    .ant-popover-content
      border-radius 12px
      box-shadow 0px 2px 10px 0px rgba(0, 0, 0, 0.06)
      overflow hidden
      border 1px solid rgba(238, 238, 238, 1)
    .ant-popover-inner-content
      padding 12px 0px 0px
    .ant-popover-arrow
      display none

  @media screen and (max-width Aries_pad_width )
    :deep(.transmission-popover)
      left var(--smallScreenLeft) !important

  @import '../styles/h5Adaptive/MainHeader.styl'
  @import '../styles/h5Adaptive/Transmission.styl'
</style>
